import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import CatalogV3Page from './ReplenishmentCatalogV3Page';
import {
  Accao, CatalogCompareRow, CatalogDetail, CatalogListResponse, CatalogMeta, CatalogParams, CatalogQuery, CatalogRow,
  CatalogRuleResult, Classificacao, Perfil, Situacao, Versao, replenishmentCatalogApi as catalogApi,
} from '../services/replenishmentCatalogApi';

const PAGE_SIZE = 50;
const STORES = ['A000', 'B000', 'C000', 'D000', 'E000', 'F000'];
const STORE_NAMES: Record<string, string> = { F000: 'Gaia' };

const CLASS_LABEL: Record<Classificacao, string> = {
  excluido_volumoso: 'Excluído (volumoso)',
  downgrade: 'Downgrade',
  sem_stock_t: 'Sem stock no T',
  omnicanal_core: 'Omnicanal core',
  omnicanal: 'Omnicanal',
  online_com_levantamento: 'Online com levantamento',
  lancamento: 'Lançamento',
  online: 'Online',
};
const CLASS_COLOR: Record<Classificacao, string> = {
  excluido_volumoso: 'dark',
  downgrade: 'danger',
  sem_stock_t: 'secondary',
  omnicanal_core: 'success',
  omnicanal: 'primary',
  online_com_levantamento: 'warning text-dark',
  lancamento: 'info',
  online: 'light text-dark border',
};
const ACCAO_LABEL: Record<Accao, string> = {
  passar_omnicanal: 'Passar a omnicanal',
  completar_lojas: 'Completar lojas',
  manter: 'Manter',
  retirar_lojas: 'Retirar das lojas',
  downgrade: 'Downgrade',
  sem_accao: 'Sem acção',
};
const ACCAO_COLOR: Record<Accao, string> = {
  passar_omnicanal: 'success',
  completar_lojas: 'warning text-dark',
  manter: 'light text-dark border',
  retirar_lojas: 'danger',
  downgrade: 'secondary',
  sem_accao: 'light text-muted border',
};
const SITUACAO_LABEL: Record<Situacao, string> = {
  online_estimado: 'Online (estimado)',
  omnicanal_parcial: 'Omnicanal parcial',
  omnicanal_completo: 'Omnicanal completo',
};
const T_PROTECTION = 'protecao_t';
const RULE_LABEL: Record<string, string> = {
  ...CLASS_LABEL,
  [T_PROTECTION]: 'Protecção do T',
};
const DEFAULT_ORDERING = 'score_desc';
const DEFAULT_MIN_DUOOS = 10;
const CAUSA_LABEL: Record<string, string> = {
  catalogacao: 'Catalogação (nenhuma loja tinha)',
  distribuicao: 'Distribuição (outras lojas tinham)',
  quantidade: 'Quantidade insuficiente',
  ruptura: 'Ruptura total',
};

const KPI_ORDER_ACCOES: Accao[] = [
  'passar_omnicanal', 'completar_lojas', 'manter', 'retirar_lojas', 'downgrade', 'sem_accao',
];
const KPI_ACCOES: Accao[] = ['passar_omnicanal', 'completar_lojas', 'retirar_lojas', 'downgrade'];
const CLASS_ORDER: Classificacao[] = [
  'omnicanal_core', 'omnicanal', 'online_com_levantamento', 'lancamento', 'online', 'sem_stock_t', 'downgrade', 'excluido_volumoso',
];

// ordenação por coluna: [padrão, alternativa]
const SORTS: Record<string, [string, string?]> = {
  sku: ['sku_asc'],
  nome: ['nome_asc'],
  preco: ['preco_desc', 'preco_asc'],
  online: ['online_desc'],
  envio: ['envio_desc'],
  levant: ['levant_desc'],
  balcao: ['balcao_desc'],
  falhas: ['falhas_desc'],
  lojas: ['lojas_asc', 'lojas_desc'],
  stock_t: ['stock_t_desc'],
  score: ['score_desc', 'score_asc'],
  duoos: ['duoos_t_asc', 'duoos_t_desc'],
  pct: ['pct_loja_desc', 'pct_loja_asc'],
};

const fmtInt = (v: number | null | undefined): string =>
  v === null || v === undefined ? '-' : v.toLocaleString('pt-PT', { maximumFractionDigits: 0 });
const fmtEur = (v: number | null | undefined): string =>
  v === null || v === undefined ? '-' : v.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' });
const fmtScore = (v: number): string =>
  v.toLocaleString('pt-PT', { minimumFractionDigits: 0, maximumFractionDigits: 1 });
const fmtDate = (iso: string): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
const fmtTimestamp = (ts: string | null): string =>
  !ts ? '-' : new Date(ts).toLocaleString('pt-PT', {
    timeZone: 'Europe/Lisbon', day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
const storeLabel = (code: string): string =>
  STORE_NAMES[code] ? `${code} · ${STORE_NAMES[code]}` : code;

const PERFIL_LABEL: Record<Perfil, string> = {
  loja: 'Loja', misto: 'Misto', online: 'Online', pouca_procura: 'Pouca procura',
};
const PERFIL_COLOR: Record<Perfil, string> = {
  loja: 'success', misto: 'info text-dark', online: 'primary', pouca_procura: 'light text-muted border',
};
const PERFIL_ORDER: Perfil[] = ['loja', 'misto', 'online', 'pouca_procura'];
const fmtPct = (v: number | null | undefined): string =>
  v === null || v === undefined ? '' : `${Math.round(v * 100)}%`;
const PerfilBadge: React.FC<{ perfil: Perfil | null; pct: number | null }> = ({ perfil, pct }) => (
  perfil ? (
    <span title="Perfil de canal (28 dias, em encomendas): % da procura que é de loja (levantamento + balcão)">
      <Badge color={PERFIL_COLOR[perfil]}>{PERFIL_LABEL[perfil]}{perfil !== 'pouca_procura' && pct !== null ? ` ${fmtPct(pct)}` : ''}</Badge>
    </span>
  ) : <span className="text-muted">-</span>
);

const DUOOS_HINT = 'Dias até o T ficar sem stock ao ritmo de venda online actual (máx. de 7d e 28d)';
const fmtDuoos = (row: { duoos_T: number | null; vel_T_7d: number | null }): string =>
  row.vel_T_7d === null || row.vel_T_7d === undefined ? '-'
    : row.duoos_T === null ? '∞' : fmtInt(Math.round(row.duoos_T));
const fmtRate = (v: number | null | undefined): string =>
  v === null || v === undefined ? '-' : `${v.toLocaleString('pt-PT', { maximumFractionDigits: 2 })} un/dia`;

/** Dropdown com checkboxes (multi-selecção). */
const MultiSelect: React.FC<{
  label: string; options: { value: string; label: string }[]; selected: string[];
  onToggle: (value: string) => void;
}> = ({ label, options, selected, onToggle }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  return (
    <div className="position-relative" ref={ref}>
      <label className="form-label small mb-1">{label}</label>
      <button type="button" className="form-select form-select-sm text-start" aria-haspopup="listbox"
              aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        {selected.length === 0 ? 'Todas' : selected.length === 1
          ? options.find((o) => o.value === selected[0])?.label ?? selected[0] : `${selected.length} seleccionadas`}
      </button>
      {open && (
        <div className="border rounded bg-white shadow-sm p-2 position-absolute w-100"
             style={{ zIndex: 1030, minWidth: 220 }} role="listbox" aria-label={label}>
          {options.map((o) => (
            <div className="form-check" key={o.value}>
              <input className="form-check-input" type="checkbox" id={`ms-${label}-${o.value}`}
                     checked={selected.includes(o.value)} onChange={() => onToggle(o.value)} />
              <label className="form-check-label small" htmlFor={`ms-${label}-${o.value}`}>{o.label}</label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Badge: React.FC<{ color: string; children: React.ReactNode; title?: string }> = ({ color, children, title }) => (
  <span className={`badge bg-${color}`} title={title} style={{ whiteSpace: 'normal' }}>{children}</span>
);
const ClassBadge: React.FC<{ value: Classificacao }> = ({ value }) => (
  <Badge color={CLASS_COLOR[value]}>{CLASS_LABEL[value] ?? value}</Badge>
);
const AccaoBadge: React.FC<{ value: Accao }> = ({ value }) => (
  <Badge color={ACCAO_COLOR[value]}>{ACCAO_LABEL[value] ?? value}</Badge>
);

/** Seis quadrados A–F: verde = loja com stock; tooltip com stock e procura. */
const StoreDots: React.FC<{ row: CatalogRow }> = ({ row }) => {
  const byStore = new Map(row.lojas.map((s) => [s.loja, s]));
  return (
    <span className="d-inline-flex gap-1" aria-label={`${row.lojas_com_stock} de 6 lojas com stock`}>
      {STORES.map((code) => {
        const s = byStore.get(code);
        const has = !!s && s.stock > 0;
        const demand = s ? s.procura_levant + s.procura_balcao : 0;
        return (
          <span
            key={code}
            title={`${storeLabel(code)}: stock ${fmtInt(s?.stock ?? 0)} · procura ${fmtInt(demand)}`}
            style={{
              width: 16, height: 16, borderRadius: 3, fontSize: 9, lineHeight: '16px', textAlign: 'center',
              color: has ? '#fff' : '#6c757d',
              background: has ? '#198754' : demand > 0 ? '#f8d7da' : '#e9ecef',
              border: !has && demand > 0 ? '1px solid #dc3545' : '1px solid transparent',
            }}
          >
            {code[0]}
          </span>
        );
      })}
    </span>
  );
};

const RulesPanelV2: React.FC<{ p: Partial<CatalogParams> }> = ({ p }) => {
  if (!p.v2_core_store_orders) return <div className="text-muted small">Parâmetros v2 indisponíveis neste snapshot.</div>;
  const j = (xs?: string[]) => (xs && xs.length ? xs.join(', ') : '-');
  const items: [string, React.ReactNode][] = [
    ['Excluído (volumoso)', <>Igual à v1 ({j(p.bulky_categories)}; TV de {p.bulky_tv_min_inches}" ou mais).</>],
    ['Downgrade', <>Igual à v1 ({j(p.downgrade_states)}).</>],
    ['Sem stock no T', <>Igual à v1: sem stock no armazém e sem compra em curso.</>],
    ['Omnicanal core', <>Perfil <strong>loja</strong> e {p.v2_core_store_orders} ou mais encomendas de loja (levantamento + balcão). Vai para <strong>as 6 lojas</strong>.</>],
    ['Omnicanal', <>Perfil loja ou misto e ({p.v2_omni_store_orders} ou mais encomendas de loja <strong>ou</strong> {p.v2_omni_pickup_failures} ou mais levantamentos falhados por causa do artigo). Só nas lojas com {p.v2_store_min_orders} ou mais encomendas de loja em {p.window_days} dias; pela via das falhas, também as lojas onde houve levantamentos falhados.</>],
    ['Online com levantamento', <>Perfil online, {p.v2_online_pickup_min} ou mais levantamentos e DUOOS do T de {p.v2_online_pickup_min_duoos} dias ou mais (sem vendas conta se há stock). 1 unidade, só nas lojas onde há levantamentos.</>],
    ['Lançamento', <>Igual à v1: criado há {p.launch_max_age_days} dias ou menos, categoria ({j(p.launch_categories)}), preço médio de {fmtEur(p.launch_min_price)} ou mais. As 6 lojas.</>],
    ['Online', <>Todos os outros artigos.</>],
  ];
  return (
    <>
      <p className="text-muted small mb-2">
        <strong>Perfil de canal</strong> ({p.window_days} dias, em encomendas): % de loja = (levantamentos + balcão) / (envio + levantamentos + balcão).
        Loja: {fmtInt((p.profile_store_min ?? 0.5) * 100)}% ou mais · Misto: {fmtInt((p.profile_mixed_min ?? 0.33) * 100)}% ou mais ·
        Online: abaixo · Pouca procura: menos de {p.profile_min_orders} encomendas. O volume de envio só conta como contexto:
        não decide se um artigo vai para loja. A acção compara as <strong>lojas-alvo</strong> com as lojas com stock hoje.
        Só se propõe <strong>retirar de uma loja</strong> com evidência forte: não é alvo, tem mais de {p.v2_remove_min_stock ?? 2} unidades
        e 0 encomendas de loja (levantamento + balcão) em {p.v2_remove_window_days ?? 90} dias.
      </p>
      <ol className="small mb-0">
        {items.map(([name, text]) => <li key={name}><strong>{name}:</strong> {text}</li>)}
      </ol>
    </>
  );
};

const RulesPanel: React.FC<{ p: Partial<CatalogParams> }> = ({ p }) => {
  if (!p.core_online_orders) return <div className="text-muted small">Parâmetros indisponíveis.</div>;
  const j = (xs?: string[]) => (xs && xs.length ? xs.join(', ') : '-');
  const items: [string, React.ReactNode][] = [
    ['Excluído (volumoso)', <>Categoria volumosa ({j(p.bulky_categories)}) ou TV de {p.bulky_tv_min_inches}" ou mais. Não cabe nas lojas.</>],
    ['Downgrade', <>Estado do artigo: {j(p.downgrade_states)}. Não se reforça.</>],
    ['Sem stock no T', <>Sem stock no armazém e sem compra a fornecedor em curso. Não se pode abastecer.</>],
    ['Omnicanal core', <>{p.core_online_orders} ou mais encomendas no site (envio + levantamento), ou {p.core_pickups} ou mais levantamentos em loja, em {p.window_days} dias.</>],
    ['Omnicanal', <>{p.omni_online_orders} ou mais encomendas no site (envio + levantamento), ou {p.omni_pickup_failures} ou mais encomendas de levantamento falhadas por causa deste artigo.</>],
    ['Lançamento', <>Criado há {p.launch_max_age_days} dias ou menos, categoria ({j(p.launch_categories)}) e preço médio de {fmtEur(p.launch_min_price)} ou mais. Omnicanal por defeito.</>],
    ['Online', <>Todos os outros artigos.</>],
    [`Protecção do T (${p.t_protection_enabled ? 'activa' : 'inactiva'})`,
      <>{fmtInt(p.t_min_duoos_days ?? DEFAULT_MIN_DUOOS)} dias: só repor lojas se o T tiver pelo menos este nº de dias de
        stock (DUOOS). {p.t_protection_enabled ? '' : 'Só informativa: não altera a classificação nem a acção.'}</>],
  ];
  return (
    <>
      <p className="text-muted small mb-2">
        Cada artigo é avaliado por esta ordem e <strong>ganha a primeira regra que se verifica</strong>.
        Janela de {p.window_days} dias até ontem; universo: artigos activos no catálogo ou com vendas nos
        últimos {p.universe_days} dias.
      </p>
      <ol className="small mb-0">
        {items.map(([name, text]) => (
          <li key={name}><strong>{name}:</strong> {text}</li>
        ))}
      </ol>
    </>
  );
};

/* --------------------------------- Detalhe --------------------------------- */

const MetricCell: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="col-6 col-md-4">
    <div className="text-muted small">{label}</div>
    <div className="fw-semibold">{value}</div>
  </div>
);

const RulesList: React.FC<{ regras: CatalogRuleResult[] | null; protectionEnabled: boolean }> = ({ regras, protectionEnabled }) => {
  if (!regras) return <p className="text-muted small">Sem avaliação neste snapshot.</p>;
  const winnerIdx = regras.findIndex((r) => r.passou && r.regra !== T_PROTECTION);
  return (
    <ol className="list-group list-group-numbered mb-3">
      {regras.map((r, i) => {
        const informative = r.regra === T_PROTECTION;
        const isWinner = i === winnerIdx;
        const decided = !informative && winnerIdx >= 0 && i > winnerIdx;
        const inactive = informative && !protectionEnabled;
        return (
          <li key={r.regra}
              className={`list-group-item ${isWinner ? 'border-success bg-success bg-opacity-10' : ''}`}
              style={decided ? { opacity: 0.55 } : undefined}>
            <div className="ms-1">
              <span>
                <strong>{RULE_LABEL[r.regra] ?? r.regra}</strong>{' '}
                <span className={r.passou ? 'text-success' : 'text-danger'}>{r.passou ? '✓' : '✗'}</span>
                {isWinner && <Badge color="success">decidiu</Badge>}
                {inactive && <> <Badge color="secondary">inactiva — só informativa</Badge></>}
                {inactive && <span className="text-muted small"> (daria {r.passou ? '✓' : '✗'})</span>}
                {r.passou && decided && <span className="text-muted small"> (verifica-se, mas já decidiu uma regra anterior)</span>}
              </span>
              <div className="small text-muted">{r.descricao}</div>
              <div className="small">
                Observado: <code>{r.valor}</code>
                {r.limiar !== '-' && <> · Limiar: <code>{r.limiar}</code></>}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
};

const StoreList: React.FC<{ label: string; stores: string[] | null | undefined; color: string }> = ({ label, stores, color }) => (
  <div className="small mb-1">
    <strong>{label}:</strong>{' '}
    {stores && stores.length > 0
      ? stores.map((s) => <span key={s} className="me-1"><Badge color={color}>{storeLabel(s)}</Badge></span>)
      : <span className="text-muted">nenhuma</span>}
  </div>
);

const DetailPanel: React.FC<{ sku: string; snapshot?: string; onClose: () => void }> = ({ sku, snapshot, onClose }) => {
  const [detail, setDetail] = useState<CatalogDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setError(null);
    catalogApi.detail(sku, snapshot)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || e?.message || 'Erro ao carregar'); });
    return () => { cancelled = true; };
  }, [sku, snapshot]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 1040 }} />
      <aside
        role="dialog"
        aria-label={`Detalhe de ${sku}`}
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(1040px, 100vw)', zIndex: 1050,
          background: '#fff', overflowY: 'auto', boxShadow: '-4px 0 16px rgba(0,0,0,.2)',
        }}
      >
        <div className="p-3 border-bottom d-flex justify-content-between align-items-start sticky-top bg-white">
          <div>
            <div className="text-muted small">{sku}</div>
            <h5 className="mb-1">{detail?.nome ?? (error ? 'Erro' : 'A carregar…')}</h5>
            {detail && (
              <div className="d-flex gap-2 flex-wrap align-items-center">
                <span className="small text-muted">v1</span>
                <ClassBadge value={detail.classificacao} />
                <AccaoBadge value={detail.accao} />
                {detail.classificacao_v2 && (
                  <>
                    <span className="small text-muted ms-2">v2</span>
                    <ClassBadge value={detail.classificacao_v2} />
                    {detail.accao_v2 && <AccaoBadge value={detail.accao_v2} />}
                  </>
                )}
                <span className="text-muted small">{SITUACAO_LABEL[detail.situacao_actual]}</span>
              </div>
            )}
          </div>
          <button type="button" className="btn-close" aria-label="Fechar" onClick={onClose} />
        </div>

        <div className="p-3">
          {error && <div className="alert alert-danger">{error}</div>}
          {!detail && !error && <div className="text-muted">A carregar…</div>}
          {detail && (
            <>
              <div className="small text-muted mb-3">
                {[detail.cat1, detail.cat2, detail.cat3].filter(Boolean).join(' › ')} · {detail.estado || 'sem estado'}
                {detail.online_catalogo && (
                  <> · <Link to={`/products/${detail.sku}`}>Ver análise do produto</Link></>
                )}
              </div>

              <h6>Perfil de canal ({detail.janela_dias} dias, em encomendas)</h6>
              {detail.perfil_canal ? (
                <div className="mb-3">
                  <div className="mb-1">
                    <PerfilBadge perfil={detail.perfil_canal} pct={detail.pct_loja} />{' '}
                    <span className="small">
                      procura em loja <strong>{fmtInt(detail.procura_loja)}</strong> (levantamento {fmtInt(detail.pedidos_levant)} +
                      balcão {fmtInt(detail.pedidos_balcao)}) contra envio <strong>{fmtInt(detail.pedidos_envio)}</strong>
                    </span>
                  </div>
                  <div className="progress" style={{ height: 14 }} title={`${fmtPct(detail.pct_loja)} loja`}>
                    <div className="progress-bar bg-success" style={{ width: `${(detail.pct_loja ?? 0) * 100}%` }}>
                      {fmtPct(detail.pct_loja)} loja
                    </div>
                    <div className="progress-bar bg-primary" style={{ width: `${100 - (detail.pct_loja ?? 0) * 100}%` }}>
                      {detail.pct_loja === null ? '' : `${100 - Math.round((detail.pct_loja ?? 0) * 100)}% envio`}
                    </div>
                  </div>
                  <div className="mt-2">
                    {detail.lojas.map((s) => {
                      const demand = s.procura_levant + s.procura_balcao;
                      const max = Math.max(1, ...detail.lojas.map((x) => x.procura_levant + x.procura_balcao));
                      return (
                        <div key={s.loja} className="d-flex align-items-center small gap-2">
                          <span style={{ width: 80 }}>{storeLabel(s.loja)}</span>
                          <div className="flex-grow-1 bg-light" style={{ height: 10 }}>
                            <div className="bg-success" style={{ height: 10, width: `${(demand / max) * 100}%` }} />
                          </div>
                          <span style={{ width: 150 }} className="text-end">
                            {fmtInt(demand)} ({fmtInt(s.procura_levant)} lev. + {fmtInt(s.procura_balcao)} balc.)
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : <p className="text-muted small">Perfil não calculado neste snapshot (correr o ETL para este dia).</p>}

              <div className="row g-3">
                <div className="col-12 col-lg-6">
                  <h6>Regras v1 — porquê esta classificação</h6>
                  <RulesList regras={detail.regras} protectionEnabled={!!detail.parametros.t_protection_enabled} />
                </div>
                <div className="col-12 col-lg-6">
                  <h6>Regras v2 — perfil de canal</h6>
                  <RulesList regras={detail.regras_v2} protectionEnabled={!!detail.parametros.t_protection_enabled} />
                  {detail.classificacao_v2 && (
                    <div className="mb-3">
                      <StoreList label="Lojas-alvo" stores={detail.lojas_alvo_v2} color="primary" />
                      <StoreList label="Lojas em falta" stores={detail.lojas_em_falta_v2} color="warning text-dark" />
                      <StoreList label="Lojas a retirar" stores={detail.lojas_a_retirar_v2} color="danger" />
                      {(detail.lojas_a_retirar_v2 ?? []).map((l) => {
                        const st = detail.lojas.find((x) => x.loja === l);
                        return (
                          <div key={l} className="small text-muted">
                            {storeLabel(l)}: {fmtInt(st?.stock)} un. em stock, não é loja-alvo e 0 encomendas de loja em
                            {' '}{detail.parametros.v2_remove_window_days ?? 90} dias.
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <h6>Métricas ({detail.janela_dias} dias até ontem)</h6>
              <div className="row g-2 mb-3">
                <MetricCell label="Envio" value={fmtInt(detail.pedidos_envio)} />
                <MetricCell label="Levantamento" value={fmtInt(detail.pedidos_levant)} />
                <MetricCell label="Balcão" value={fmtInt(detail.pedidos_balcao)} />
                <MetricCell label="Total site (envio + levant.)" value={fmtInt(detail.pedidos_online)} />
                <MetricCell label="Unidades online" value={fmtInt(detail.unidades_online)} />
                <MetricCell label="Preço médio" value={fmtEur(detail.preco_medio)} />
                <MetricCell label="Stock no T" value={fmtInt(detail.stock_T)} />
                <MetricCell label="Vel. T 7d" value={fmtRate(detail.vel_T_7d)} />
                <MetricCell label="Vel. T 28d" value={fmtRate(detail.vel_T_28d)} />
                <MetricCell label={`DUOOS T (${DUOOS_HINT})`} value={detail.vel_T_7d === null ? '-' : `${fmtDuoos(detail)}${detail.duoos_T === null ? '' : ' dias'}`} />
                <MetricCell label="DUOOS T (com lojas: envio + levant. + balcão)" value={
                  detail.vel_T_7d === null ? '-' : detail.duoos_T_total === null ? '∞' : `${fmtInt(Math.round(detail.duoos_T_total))} dias`} />
                <MetricCell label="Compra em curso (T)" value={fmtInt(detail.compra_em_curso_T)} />
                <MetricCell label="Em trânsito (T)" value={fmtInt(detail.transito_T)} />
                <MetricCell label="Criado há" value={detail.dias_desde_criacao === null ? '-' : `${fmtInt(detail.dias_desde_criacao)} dias`} />
                <MetricCell label="Lojas com stock" value={`${detail.lojas_com_stock} / 6`} />
                <MetricCell label="Score" value={fmtScore(detail.score)} />
              </div>

              <h6>Procura e stock por loja</h6>
              <table className="table table-sm mb-3">
                <thead>
                  <tr><th>Loja</th><th className="text-end">Stock</th><th className="text-end">Levantamentos</th>
                    <th className="text-end">Balcão</th>
                    <th className="text-end" title="Levantamentos + balcão na janela de 90 dias (usado para decidir retirar das lojas)">Procura {detail.parametros.v2_remove_window_days ?? 90}d</th>
                    <th className="text-end">Falhas</th><th /></tr>
                </thead>
                <tbody>
                  {detail.lojas.map((s) => (
                    <tr key={s.loja}>
                      <td>{storeLabel(s.loja)}</td>
                      <td className={`text-end ${s.stock > 0 ? '' : 'text-danger'}`}>{fmtInt(s.stock)}</td>
                      <td className="text-end">{fmtInt(s.procura_levant)}</td>
                      <td className="text-end">{fmtInt(s.procura_balcao)}</td>
                      <td className="text-end">{fmtInt(detail.procura_loja_90d_por_loja?.find((d) => d.loja === s.loja)?.procura)}</td>
                      <td className="text-end">{fmtInt(s.falhas)}</td>
                      <td>
                        {detail.lojas_em_falta.includes(s.loja) && <Badge color="warning text-dark">em falta</Badge>}
                        {detail.lojas_a_retirar_v2?.includes(s.loja) && (
                          <span title="Retirar: não é loja-alvo, stock acima do mínimo e sem procura em loja na janela longa">
                            <Badge color="danger">a retirar (v2)</Badge>
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <h6>Falhas de levantamento por causa</h6>
              {detail.falhas_levant === null ? (
                <p className="text-muted small">Sem dados de falhas neste snapshot.</p>
              ) : (
                <ul className="small">
                  <li><strong>Total:</strong> {fmtInt(detail.falhas_levant)} encomendas</li>
                  {(['catalogacao', 'distribuicao', 'quantidade', 'ruptura'] as const).map((c) => (
                    <li key={c}>{CAUSA_LABEL[c]}: {fmtInt(detail[`falhas_${c}` as const])}</li>
                  ))}
                </ul>
              )}

              <h6>Últimas encomendas falhadas por causa deste artigo</h6>
              {detail.encomendas_falhadas.length === 0 ? (
                <p className="text-muted small">Nenhuma nos últimos 90 dias.</p>
              ) : (
                <table className="table table-sm mb-3">
                  <thead><tr><th>Data</th><th>Loja</th><th>Encomenda</th><th className="text-end">Qtd</th>
                    <th className="text-end">Stock loja</th><th className="text-end">Stock T</th><th>Causa</th></tr></thead>
                  <tbody>
                    {detail.encomendas_falhadas.map((o) => (
                      <tr key={`${o.doc}-${o.loja}`}>
                        <td>{fmtDate(o.order_date)}</td>
                        <td>{o.loja}</td>
                        <td>
                          <Link to={`/replenishment?store=${encodeURIComponent(o.loja)}&doc=${encodeURIComponent(o.doc)}`}>
                            {o.external_ref || o.doc}
                          </Link>
                        </td>
                        <td className="text-end">{fmtInt(o.qty)}</td>
                        <td className="text-end">{fmtInt(o.stock_loja)}</td>
                        <td className="text-end">{fmtInt(o.stock_t)}</td>
                        <td>{CAUSA_LABEL[o.causa] ?? o.causa}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <h6>Histórico da classificação</h6>
              <table className="table table-sm mb-3">
                <thead><tr><th>Dia</th><th>Classificação</th><th>Acção</th><th className="text-end">Total site</th>
                  <th className="text-end">Lojas</th></tr></thead>
                <tbody>
                  {detail.historico.map((h, i) => {
                    const prev = detail.historico[i + 1];
                    const changed = prev && prev.classificacao !== h.classificacao;
                    return (
                      <tr key={h.snapshot_date} className={changed ? 'table-warning' : ''}>
                        <td>{fmtDate(h.snapshot_date)}</td>
                        <td><ClassBadge value={h.classificacao} /></td>
                        <td><AccaoBadge value={h.accao} /></td>
                        <td className="text-end">{fmtInt(h.pedidos_online)}</td>
                        <td className="text-end">{h.lojas_com_stock}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <details className="mb-4">
                <summary className="small text-muted">Parâmetros usados neste snapshot</summary>
                <pre className="small bg-light p-2 mt-2">{JSON.stringify(detail.parametros, null, 2)}</pre>
              </details>
              <div className="text-muted small">
                Snapshot {fmtDate(detail.snapshot_date)} · calculado em {fmtTimestamp(detail.calculated_at)}
                {detail.catalogacao_sap ? ` · catalogação SAP: ${detail.catalogacao_sap}` : ''}
              </div>
            </>
          )}
        </div>
      </aside>
    </>
  );
};

/* --------------------------- Matriz de comparação --------------------------- */

const CompareMatrix: React.FC<{
  rows: CatalogCompareRow[]; kind: 'cls' | 'acc'; order: string[]; labels: Record<string, string>;
  selected: { v1?: string; v2?: string }; onPick: (v1?: string, v2?: string) => void;
}> = ({ rows, kind, order, labels, selected, onPick }) => {
  const key1 = kind === 'cls' ? 'cls_v1' : 'acc_v1';
  const key2 = kind === 'cls' ? 'cls_v2' : 'acc_v2';
  const cells = new Map<string, number>();
  rows.forEach((r) => {
    const k = `${r[key1]}|${r[key2]}`;
    cells.set(k, (cells.get(k) ?? 0) + r.n);
  });
  const rowTotal = (a: string) => order.reduce((t, b) => t + (cells.get(`${a}|${b}`) ?? 0), 0);
  const colTotal = (b: string) => order.reduce((t, a) => t + (cells.get(`${a}|${b}`) ?? 0), 0);
  return (
    <div className="table-responsive">
      <table className="table table-sm table-bordered small mb-0 text-end" aria-label={`Matriz ${kind}`}>
        <thead className="table-light">
          <tr><th className="text-start">v1 ↓ / v2 →</th>
            {order.map((b) => <th key={b}>{labels[b]}</th>)}<th>Total v1</th></tr>
        </thead>
        <tbody>
          {order.map((a) => (
            <tr key={a}>
              <th className="text-start table-light">{labels[a]}</th>
              {order.map((b) => {
                const n = cells.get(`${a}|${b}`) ?? 0;
                const active = selected.v1 === a && selected.v2 === b;
                return (
                  <td key={b}
                      className={`${a === b ? 'text-muted' : n > 0 ? 'fw-semibold' : 'text-muted'} ${active ? 'table-primary' : a !== b && n > 0 ? 'table-warning' : ''}`}
                      style={{ cursor: n > 0 ? 'pointer' : 'default' }}
                      title={`${labels[a]} (v1) → ${labels[b]} (v2)`}
                      onClick={() => n > 0 && (active ? onPick(undefined, undefined) : onPick(a, b))}>
                    {n > 0 ? fmtInt(n) : '·'}
                  </td>
                );
              })}
              <td className="fw-semibold">{fmtInt(rowTotal(a))}</td>
            </tr>
          ))}
          <tr className="table-light"><th className="text-start">Total v2</th>
            {order.map((b) => <td key={b} className="fw-semibold">{fmtInt(colTotal(b))}</td>)}<td /></tr>
        </tbody>
      </table>
    </div>
  );
};

/* ---------------------------------- Página ---------------------------------- */

const CatalogLegacyPage: React.FC = () => {
  const [meta, setMeta] = useState<CatalogMeta | null>(null);
  const [params, setParams] = useSearchParams();
  const list = (key: string): string[] => (params.get(key) || '').split(',').filter(Boolean);
  const classFilter = list('classificacao');
  const accaoFilter = list('accao');
  const situacao = params.get('situacao') || '';
  const cat1 = params.get('cat1') || '';
  const estado = params.get('estado') || '';
  const search = params.get('q') || '';
  const ordering = params.get('ordering') || DEFAULT_ORDERING;
  const duoosMax = params.get('duoos_max') ? Number(params.get('duoos_max')) : undefined;
  const versao: Versao = params.get('versao') === 'v2' ? 'v2' : 'v1';
  const perfilFilter = list('perfil');
  const pctMin = params.get('pct_loja_min') ? Number(params.get('pct_loja_min')) : undefined;
  const pctMax = params.get('pct_loja_max') ? Number(params.get('pct_loja_max')) : undefined;
  const clsV1 = params.get('cls_v1') || '';
  const clsV2 = params.get('cls_v2') || '';
  const accV1 = params.get('acc_v1') || '';
  const accV2 = params.get('acc_v2') || '';
  const mudou = params.get('mudou') === '1';
  const showCompare = params.get('comparar') === '1';
  const page = Math.max(0, (parseInt(params.get('page') || '1', 10) || 1) - 1);
  const selectedSku = params.get('sku');
  const snapshot = params.get('snapshot') || meta?.snapshots[0]?.snapshot_date || '';
  const [searchInput, setSearchInput] = useState(search);

  /** Actualiza o URL (push: o botão voltar funciona). Valores vazios/por defeito não ficam no URL. */
  const update = useCallback((patch: Record<string, string | undefined>, opts?: { keepPage?: boolean; replace?: boolean }) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (!opts?.keepPage && !('page' in patch)) next.delete('page');
      Object.entries(patch).forEach(([k, v]) => {
        const isDefault = (k === 'ordering' && v === DEFAULT_ORDERING) || (k === 'page' && v === '1');
        if (!v || isDefault) next.delete(k); else next.set(k, v);
      });
      return next;
    }, { replace: opts?.replace });
  }, [setParams]);
  const [data, setData] = useState<CatalogListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRules, setShowRules] = useState(false);
  const requestId = useRef(0);

  useEffect(() => {
    catalogApi.meta()
      .then((m) => setMeta(m))
      .catch((e) => setError(e?.response?.data?.error || e?.message || 'Erro ao carregar metadados'));
  }, []);

  // pesquisa com debounce -> URL (replace, para não encher o histórico)
  useEffect(() => {
    const value = searchInput.trim();
    if (value === search) return undefined;
    const t = setTimeout(() => update({ q: value || undefined }, { replace: true }), 350);
    return () => clearTimeout(t);
  }, [searchInput, search, update]);
  // voltar/avançar no browser: repõe o texto da pesquisa
  useEffect(() => { setSearchInput(search); }, [search]);

  const query: CatalogQuery = useMemo(() => ({
    snapshot: snapshot || undefined,
    classificacao: classFilter,
    accao: accaoFilter,
    situacao: situacao ? [situacao] : [],
    cat1: cat1 || undefined,
    estado: estado || undefined,
    search: search || undefined,
    duoos_max: duoosMax,
    versao,
    perfil: perfilFilter,
    pct_loja_min: pctMin,
    pct_loja_max: pctMax,
    cls_v1: clsV1 || undefined,
    cls_v2: clsV2 || undefined,
    acc_v1: accV1 || undefined,
    acc_v2: accV2 || undefined,
    mudou,
    ordering,
  }), [snapshot, params]);  // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    if (!snapshot) return;
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const res = await catalogApi.list({ ...query, limit: PAGE_SIZE, offset: page * PAGE_SIZE });
      if (id === requestId.current) setData(res);
    } catch (e: any) {
      if (id === requestId.current) setError(e?.response?.data?.error || e?.message || 'Erro ao carregar dados');
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [query, page, snapshot]);

  useEffect(() => { load(); }, [load]);

  // matriz v1 x v2 (só quando o painel está aberto); ignora os filtros de classificação/acção
  const [matrix, setMatrix] = useState<CatalogCompareRow[]>([]);
  const compareKey = JSON.stringify([snapshot, situacao, cat1, estado, search, duoosMax, perfilFilter, pctMin, pctMax]);
  useEffect(() => {
    if (!showCompare || !snapshot) return;
    let cancelled = false;
    catalogApi.compare({
      snapshot, situacao: situacao ? [situacao] : [], cat1: cat1 || undefined, estado: estado || undefined,
      search: search || undefined, duoos_max: duoosMax, perfil: perfilFilter, pct_loja_min: pctMin, pct_loja_max: pctMax,
    }).then((r) => { if (!cancelled) setMatrix(r.rows); }).catch(() => { if (!cancelled) setMatrix([]); });
    return () => { cancelled = true; };
  }, [compareKey, showCompare]);  // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (key: 'classificacao' | 'accao' | 'perfil', current: string[], value: string) => {
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    update({ [key]: next.join(',') || undefined });
  };
  const resetFilters = () => {
    setSearchInput('');
    update({ classificacao: undefined, accao: undefined, situacao: undefined, cat1: undefined,
      estado: undefined, q: undefined, duoos_max: undefined, perfil: undefined, pct_loja_min: undefined,
      pct_loja_max: undefined, cls_v1: undefined, cls_v2: undefined, acc_v1: undefined, acc_v2: undefined,
      mudou: undefined });
  };
  const hasFilters = classFilter.length + accaoFilter.length > 0
    || !!(situacao || cat1 || estado || search) || duoosMax !== undefined
    || perfilFilter.length > 0 || pctMin !== undefined || pctMax !== undefined
    || !!(clsV1 || clsV2 || accV1 || accV2) || mudou;
  const minDuoos = meta?.parametros.t_min_duoos_days ?? DEFAULT_MIN_DUOOS;
  const setPage = (fn: number | ((p: number) => number)) => {
    const n = typeof fn === 'function' ? fn(page) : fn;
    update({ page: String(n + 1) }, { keepPage: true });
  };

  const sortBy = (col: string) => {
    const [first, second] = SORTS[col];
    update({ ordering: ordering === first && second ? second : first });
  };
  const sortMark = (col: string): string => {
    const [first, second] = SORTS[col];
    if (ordering === first) return first.endsWith('_asc') ? ' ▲' : ' ▼';
    if (second && ordering === second) return second.endsWith('_asc') ? ' ▲' : ' ▼';
    return '';
  };
  const Th: React.FC<{ col: string; className?: string; title?: string; children: React.ReactNode }> = ({ col, className, title, children }) => (
    <th className={className} title={title} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => sortBy(col)}>
      {children}{sortMark(col)}
    </th>
  );

  const total = data?.count ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
        <div>
          <h2 className="mb-1">🏬 Replenishment — Catalogação omnicanal / online</h2>
          <p className="text-muted small mb-2">
            Como o modelo cataloga cada artigo e porquê. A situação actual é <strong>estimada</strong> pelo
            nº de lojas com stock (o campo omnicanal do SAP ainda não está no data lake).{' '}
            <Link to="/replenishment">Hit rate das lojas</Link>
          </p>
        </div>
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <div className="btn-group btn-group-sm" role="group" aria-label="Versão das regras">
            <span className="input-group-text border-0 bg-transparent small">Regras:</span>
            {(['v1', 'v2', 'v3'] as Versao[]).map((v) => (
              <button key={v} type="button" aria-pressed={versao === v}
                      className={`btn ${versao === v ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => (v === 'v3'
                        // v3 tem filtros e ordenações próprios: recomeça limpo, mantendo o snapshot
                        ? setParams((prev) => {
                          const next = new URLSearchParams();  // v3 é a versão por omissão: sem `versao` no URL
                          if (prev.get('snapshot')) next.set('snapshot', prev.get('snapshot') as string);
                          return next;
                        })
                        : update({ versao: v }))}>{v}</button>
            ))}
          </div>
          <select className="form-select form-select-sm" value={snapshot} aria-label="Snapshot"
                  onChange={(e) => update({ snapshot: e.target.value === meta?.snapshots[0]?.snapshot_date ? undefined : e.target.value })}>
            {meta?.snapshots.map((s) => (
              <option key={s.snapshot_date} value={s.snapshot_date}>
                {fmtDate(s.snapshot_date)} ({fmtInt(s.skus)} SKUs)
              </option>
            ))}
          </select>
          <a className={`btn btn-sm btn-outline-secondary ${!snapshot ? 'disabled' : ''}`}
             href={catalogApi.exportUrl(query)}>
            ⬇ CSV
          </a>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* KPIs: acções */}
      <div className="row g-2 mb-2">
        {KPI_ACCOES.map((a) => (
          <div className="col-6 col-lg-3" key={a}>
            <div className={`card h-100 ${accaoFilter.includes(a) ? 'border-primary border-2' : ''}`}
                 role="button" onClick={() => toggle('accao', accaoFilter, a)}>
              <div className="card-body py-2">
                <div className="text-muted small">{ACCAO_LABEL[a]}</div>
                <div className="fs-3 fw-semibold">{fmtInt(data?.counts_accao[a])}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* KPIs: classificação */}
      <div className="d-flex flex-wrap gap-2 mb-3">
        {CLASS_ORDER.filter((c) => versao === 'v2' || c !== 'online_com_levantamento').map((c) => (
          <button key={c} type="button"
                  className={`btn btn-sm ${classFilter.includes(c) ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => toggle('classificacao', classFilter, c)}>
            {CLASS_LABEL[c]} <strong>{fmtInt(data?.counts_classificacao[c])}</strong>
          </button>
        ))}
        <button type="button" className={`btn btn-sm ${showCompare ? 'btn-secondary' : 'btn-outline-secondary'}`}
                onClick={() => update({ comparar: showCompare ? undefined : '1' }, { keepPage: true })}>
          Comparar v1 × v2
        </button>
        <button type="button" className="btn btn-sm btn-link" onClick={() => setShowRules((v) => !v)}>
          {showRules ? 'Esconder regras em vigor' : 'Regras em vigor'}
        </button>
      </div>

      {showRules && (
        <div className="card mb-3"><div className="card-body">
          <h6 className="card-title">Regras em vigor ({versao})</h6>
          {versao === 'v2' ? <RulesPanelV2 p={meta?.parametros ?? {}} /> : <RulesPanel p={meta?.parametros ?? {}} />}
        </div></div>
      )}

      {showCompare && (
        <div className="card mb-3"><div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="card-title mb-0">Comparar v1 × v2 (clicar numa célula filtra a tabela)</h6>
            <div className="d-flex gap-2 align-items-center">
              <div className="form-check mb-0">
                <input className="form-check-input" type="checkbox" id="mudou-filter" checked={mudou}
                       onChange={(e) => update({ mudou: e.target.checked ? '1' : undefined, cls_v1: undefined, cls_v2: undefined })} />
                <label className="form-check-label small" htmlFor="mudou-filter">Só os que mudaram de classificação</label>
              </div>
              {(clsV1 || clsV2 || accV1 || accV2) && (
                <button className="btn btn-sm btn-link p-0"
                        onClick={() => update({ cls_v1: undefined, cls_v2: undefined, acc_v1: undefined, acc_v2: undefined })}>
                  Limpar célula
                </button>
              )}
            </div>
          </div>
          <h6 className="small text-muted">Classificação</h6>
          <CompareMatrix rows={matrix} kind="cls" order={CLASS_ORDER} labels={CLASS_LABEL}
                         selected={{ v1: clsV1, v2: clsV2 }}
                         onPick={(a, b) => update({ cls_v1: a, cls_v2: b, mudou: undefined })} />
          <h6 className="small text-muted mt-3">Acção</h6>
          <CompareMatrix rows={matrix} kind="acc" order={KPI_ORDER_ACCOES} labels={ACCAO_LABEL}
                         selected={{ v1: accV1, v2: accV2 }}
                         onPick={(a, b) => update({ acc_v1: a, acc_v2: b })} />
        </div></div>
      )}

      {/* Filtros */}
      <div className="card mb-3"><div className="card-body py-2">
        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-3">
            <label className="form-label small mb-1" htmlFor="catalog-search">Pesquisa (SKU ou nome)</label>
            <input id="catalog-search" className="form-control form-control-sm" value={searchInput}
                   onChange={(e) => setSearchInput(e.target.value)} placeholder="P040414, iPhone…" />
          </div>
          <div className="col-6 col-md-2">
            <MultiSelect label="Acção" selected={accaoFilter}
                         options={KPI_ORDER_ACCOES.map((a) => ({ value: a, label: ACCAO_LABEL[a] }))}
                         onToggle={(v) => toggle('accao', accaoFilter, v)} />
          </div>
          <div className="col-6 col-md-2">
            <MultiSelect label="Classificação" selected={classFilter}
                         options={CLASS_ORDER.map((c) => ({ value: c, label: CLASS_LABEL[c] }))}
                         onToggle={(v) => toggle('classificacao', classFilter, v)} />
          </div>
          <div className="col-6 col-md-2">
            <label className="form-label small mb-1">Situação actual</label>
            <select className="form-select form-select-sm" value={situacao}
                    onChange={(e) => update({ situacao: e.target.value || undefined })}>
              <option value="">Todas</option>
              {(meta?.situacoes ?? []).map((s) => <option key={s} value={s}>{SITUACAO_LABEL[s]}</option>)}
            </select>
          </div>
          <div className="col-6 col-md-2">
            <label className="form-label small mb-1">Categoria 1</label>
            <select className="form-select form-select-sm" value={cat1}
                    onChange={(e) => update({ cat1: e.target.value || undefined })}>
              <option value="">Todas</option>
              {(meta?.cat1 ?? []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="col-6 col-md-2">
            <label className="form-label small mb-1">Estado</label>
            <select className="form-select form-select-sm" value={estado}
                    onChange={(e) => update({ estado: e.target.value || undefined })}>
              <option value="">Todos</option>
              {(meta?.estados ?? []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="col-6 col-md-2">
            <MultiSelect label="Perfil de canal" selected={perfilFilter}
                         options={PERFIL_ORDER.map((x) => ({ value: x, label: PERFIL_LABEL[x] }))}
                         onToggle={(v) => toggle('perfil', perfilFilter, v)} />
          </div>
          <div className="col-6 col-md-2">
            <label className="form-label small mb-1" htmlFor="pct-min">% loja (mín – máx)</label>
            <div className="input-group input-group-sm">
              <input id="pct-min" type="number" min={0} max={100} className="form-control" placeholder="mín"
                     value={pctMin === undefined ? '' : Math.round(pctMin * 100)}
                     onChange={(e) => update({ pct_loja_min: e.target.value === '' ? undefined : String(Number(e.target.value) / 100) })} />
              <input aria-label="% loja máx" type="number" min={0} max={100} className="form-control" placeholder="máx"
                     value={pctMax === undefined ? '' : Math.round(pctMax * 100)}
                     onChange={(e) => update({ pct_loja_max: e.target.value === '' ? undefined : String(Number(e.target.value) / 100) })} />
            </div>
          </div>
          <div className="col-6 col-md-2">
            <div className="form-check mt-3">
              <input className="form-check-input" type="checkbox" id="duoos-filter" checked={duoosMax !== undefined}
                     onChange={(e) => update({ duoos_max: e.target.checked ? String(minDuoos) : undefined })} />
              <label className="form-check-label small" htmlFor="duoos-filter" title={DUOOS_HINT}>
                T com &lt; {fmtInt(minDuoos)} dias
              </label>
            </div>
          </div>
          <div className="col-12 col-md-3 d-flex gap-2 align-items-center">
            <span className="text-muted small">
              {loading ? 'A carregar…' : `${fmtInt(total)} artigos`}
              {data?.calculated_at && ` · calculado ${fmtTimestamp(data.calculated_at)}`}
            </span>
            {hasFilters && <button className="btn btn-sm btn-link p-0" onClick={resetFilters}>Limpar filtros</button>}
          </div>
        </div>
        {(mudou || clsV1 || accV1) && (
          <div className="mt-2 small text-muted">
            Comparação v1 → v2:{' '}
            {mudou && <Badge color="warning text-dark">mudou de classificação</Badge>}
            {clsV1 && clsV2 && <>{' '}<ClassBadge value={clsV1 as Classificacao} /> → <ClassBadge value={clsV2 as Classificacao} /></>}
            {accV1 && accV2 && <>{' '}<AccaoBadge value={accV1 as Accao} /> → <AccaoBadge value={accV2 as Accao} /></>}
          </div>
        )}
        {(classFilter.length > 0 || accaoFilter.length > 0) && (
          <div className="mt-2 small text-muted">
            Filtros activos:{' '}
            {classFilter.map((c) => <span key={c} className="me-1"><ClassBadge value={c as Classificacao} /></span>)}
            {accaoFilter.map((a) => <span key={a} className="me-1"><AccaoBadge value={a as Accao} /></span>)}
          </div>
        )}
      </div></div>

      {/* Tabela */}
      <div className="table-responsive" style={{ opacity: loading ? 0.6 : 1 }}>
        <table className="table table-sm table-hover align-middle small">
          <thead className="table-light">
            <tr>
              <Th col="sku">SKU</Th>
              <Th col="nome">Nome</Th>
              <th>Categoria</th>
              <th>Estado</th>
              <Th col="pct" title="Perfil de canal: % da procura (encomendas, 28d) que é de loja">Perfil</Th>
              <Th col="preco" className="text-end">Preço</Th>
              <Th col="envio" className="text-end" title="Encomendas no site com envio ao cliente (a partir do T000), 28 dias">Envio</Th>
              <Th col="levant" className="text-end" title="Encomendas no site com levantamento em loja, 28 dias">Levant.</Th>
              <Th col="balcao" className="text-end" title="Vendas ao balcão nas lojas (sem encomenda no site), 28 dias">Balcão</Th>
              <Th col="online" className="text-end" title="Total de encomendas no site = Envio + Levant. (usado nas regras)">Total site</Th>
              <Th col="falhas" className="text-end">Falhas</Th>
              <Th col="lojas">Lojas</Th>
              <Th col="stock_t" className="text-end">Stock T</Th>
              <Th col="duoos" className="text-end" title={DUOOS_HINT}>DUOOS T</Th>
              <th>Situação</th>
              <th>Classificação</th>
              <th>Acção</th>
              <th>Regra</th>
              <Th col="score" className="text-end">Score</Th>
            </tr>
          </thead>
          <tbody>
            {data?.results.map((r) => (
              <tr key={r.sku} role="button" onClick={() => update({ sku: r.sku }, { keepPage: true })}>
                <td><code>{r.sku}</code></td>
                <td style={{ maxWidth: 320 }}>{r.nome ?? '-'}</td>
                <td>{r.cat1}<div className="text-muted">{r.cat2}</div></td>
                <td>{r.estado || '-'}</td>
                <td><PerfilBadge perfil={r.perfil_canal} pct={r.pct_loja} /></td>
                <td className="text-end">{fmtEur(r.preco_medio)}</td>
                <td className="text-end">{fmtInt(r.pedidos_envio)}</td>
                <td className="text-end">{fmtInt(r.pedidos_levant)}</td>
                <td className="text-end">{fmtInt(r.pedidos_balcao)}</td>
                <td className="text-end fw-semibold">{fmtInt(r.pedidos_online)}</td>
                <td className="text-end">{fmtInt(r.falhas_levant)}</td>
                <td><StoreDots row={r} /> <span className="text-muted">{r.lojas_com_stock}/6</span></td>
                <td className="text-end">{fmtInt(r.stock_T)}</td>
                <td className="text-end fw-semibold" title={DUOOS_HINT}
                    style={{ color: r.duoos_T === null ? undefined : r.duoos_T < minDuoos ? '#dc3545'
                      : r.duoos_T < 2 * minDuoos ? '#fd7e14' : undefined }}>
                  {r.vel_T_7d === null ? '-' : r.duoos_T === null ? <span title="∞ (sem vendas)">∞</span> : fmtDuoos(r)}
                </td>
                <td>{SITUACAO_LABEL[r.situacao_actual]}</td>
                <td><ClassBadge value={r.classificacao} /></td>
                <td title={versao === 'v2' && r.lojas_alvo_v2
                  ? `Lojas-alvo: ${r.lojas_alvo_v2.join(', ') || '-'} · em falta: ${(r.lojas_em_falta_v2 ?? []).join(', ') || '-'} · a retirar: ${(r.lojas_a_retirar_v2 ?? []).join(', ') || '-'}`
                  : undefined}><AccaoBadge value={r.accao} /></td>
                <td className="text-muted">{CLASS_LABEL[r.regra_disparada as Classificacao] ?? r.regra_disparada}</td>
                <td className="text-end">{fmtScore(r.score)}</td>
              </tr>
            ))}
            {data && data.results.length === 0 && (
              <tr><td colSpan={18} className="text-center text-muted py-4">Sem artigos para os filtros escolhidos.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="d-flex justify-content-between align-items-center">
        <span className="text-muted small">Página {page + 1} de {fmtInt(pages)}</span>
        <div className="btn-group btn-group-sm">
          <button className="btn btn-outline-secondary" disabled={page === 0 || loading}
                  onClick={() => setPage(0)}>«</button>
          <button className="btn btn-outline-secondary" disabled={page === 0 || loading}
                  onClick={() => setPage((p) => p - 1)}>‹ Anterior</button>
          <button className="btn btn-outline-secondary" disabled={page + 1 >= pages || loading}
                  onClick={() => setPage((p) => p + 1)}>Seguinte ›</button>
        </div>
      </div>

      {selectedSku && (
        <DetailPanel sku={selectedSku} snapshot={snapshot || undefined} onClose={() => update({ sku: undefined }, { keepPage: true })} />
      )}
    </div>
  );
};

/**
 * v3 (D1/D2) é a versão por omissão e tem tabela, filtros e export próprios; `?versao=v1|v2` explícito
 * mantém a vista de regras/acções (links antigos continuam a funcionar).
 */
const ReplenishmentCatalogPage: React.FC = () => {
  const [params] = useSearchParams();
  const versao = params.get('versao');
  return versao === 'v1' || versao === 'v2' ? <CatalogLegacyPage /> : <CatalogV3Page />;
};

export default ReplenishmentCatalogPage;
